import axios, { AxiosRequestConfig } from "axios";
import { appConst } from "../AppConst";
import { IBaseRespone } from "../model/respone/IBaseRespone";

/**
 * Default request timeout (ms). Server hang → axios reject với code "ECONNABORTED"
 * thay vì user chờ vô thời hạn. 30s đủ cho slow query (eligibility 200 lớp ~3s) + buffer.
 * Override per-call qua `config.timeout` nếu endpoint nặng hơn (vd export Excel).
 */
const DEFAULT_TIMEOUT_MS = 30000;

export const saveAccessToken = (token: string) => {
    localStorage.setItem("access_token", token);
};

export const getAccessToken = (): string | null => {
    return localStorage.getItem("access_token");
};

export const clearAccessToken = () => {
    localStorage.removeItem("access_token");
};

// ── Single-device login: sid phiên hiện tại ────────────────────────────────
// Lưu để lọc event ForceLogout của chính mình (login mới đẩy ForceLogout kèm sid mới; nếu trùng
// sid đang giữ thì bỏ qua, không tự đá). undefined khi backend tắt Auth:SingleSession.
export const saveSessionId = (sid?: string) => {
    if (sid) localStorage.setItem("session_id", sid);
    else localStorage.removeItem("session_id");
};

export const getSessionId = (): string | null => {
    return localStorage.getItem("session_id");
};

export const clearSessionId = () => {
    localStorage.removeItem("session_id");
};

// ── Multi-period: đợt đăng ký đang chọn ─────────────────────────────────────
// Backend yêu cầu header X-Period-Id trên mọi /api/registration/* + /api/queue/*.
// Lưu module-level + localStorage → mọi request tự gắn header, không cần sửa từng API.
const PERIOD_KEY = "active_period_id";
let activePeriodId: number | null = (() => {
    const raw = localStorage.getItem(PERIOD_KEY);
    return raw && !isNaN(Number(raw)) ? Number(raw) : null;
})();

// Pub/sub để React component (Header, Landing, Classes) phản ứng khi đợt đang chọn đổi —
// tránh race lúc login: component chờ period được set (Hub adopt / bootstrap) rồi mới gọi API.
const periodListeners = new Set<() => void>();
export const onActivePeriodChange = (fn: () => void): (() => void) => {
    periodListeners.add(fn);
    return () => { periodListeners.delete(fn); };
};
const notifyPeriod = () => periodListeners.forEach(fn => { try { fn(); } catch { /* ignore */ } });

// Đã resolve xong danh sách đợt chưa (kể cả khi rỗng) — phân biệt "đang resolve" vs "không có đợt"
// để Landing/Classes không kẹt spinner cho SV ngoài phạm vi mọi đợt.
let periodResolved = false;
export const isPeriodResolved = (): boolean => periodResolved;
export const markPeriodResolved = () => { if (!periodResolved) { periodResolved = true; notifyPeriod(); } };

export const setActivePeriodId = (id: number | null) => {
    const next = id && id > 0 ? id : null;
    // Logout/401 truyền null → reset cờ resolved để phiên sau resolve lại từ đầu.
    if (next === null) periodResolved = false;
    if (next === activePeriodId) return;
    activePeriodId = next;
    if (activePeriodId) localStorage.setItem(PERIOD_KEY, String(activePeriodId));
    else localStorage.removeItem(PERIOD_KEY);
    notifyPeriod();
};

export const getActivePeriodId = (): number | null => activePeriodId;

const buildHeaders = (extra?: Record<string, string>) => {
    const token = getAccessToken();
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(activePeriodId ? { "X-Period-Id": String(activePeriodId) } : {}),
        ...(extra || {}),
    };
};

const buildConfig = (extraHeaders?: Record<string, string>): AxiosRequestConfig => ({
    headers: buildHeaders(extraHeaders),
    timeout: DEFAULT_TIMEOUT_MS,
});

// Multi-period guard tập trung: endpoint nghiệp vụ BẮT BUỘC có đợt đang chọn. Chặn sớm ở client
// thay vì để request thiếu X-Period-Id rơi xuống backend → 400 PERIOD_REQUIRED (noise + race).
// Ngoại lệ: registration/periods là endpoint RESOLVE đợt — phải gọi được KHI CHƯA có đợt.
// Khi đợt resolve xong, listener onActivePeriodChange + effect ở caller (deps activePeriodId) tự
// re-dispatch → trạng thái is_error (nếu có) chỉ thoáng qua rồi tự phục hồi.
const requiresPeriod = (path: string): boolean => {
    const p = path.split("?")[0];
    if (p === "registration/periods" || p.startsWith("registration/periods/")) return false;
    return p.startsWith("registration/") || p.startsWith("queue/") || p === "queue";
};

const periodGuard = <T = any>(path: string): IBaseRespone<T> | null => {
    if (requiresPeriod(path) && !activePeriodId) {
        return { is_success: false, status_code: 400, message: "Chưa chọn đợt đăng ký (PERIOD_REQUIRED)." } as IBaseRespone<T>;
    }
    return null;
};

interface IRequestConfig {
    headers?: Record<string, string>;
}

const handleResponse = async <T = any>(res: any): Promise<IBaseRespone<T>> => {
    return res.data as IBaseRespone<T>;
};

const handleError = (err: any): IBaseRespone => {
    if (err?.response?.status === 401) {
        clearAccessToken();
        setActivePeriodId(null);
        if (window.location.pathname !== "/login") {
            window.location.href = "/login";
        }
        return { is_success: false, status_code: 401, message: "Phiên đăng nhập đã hết hạn." };
    }
    // 429 Too Many Requests — rate limit. Đè message server bằng câu rõ ràng cho người dùng cuối,
    // áp dụng cho mọi endpoint (classes, register, ...). Server gốc: "...đợi giây lát".
    if (err?.response?.status === 429) {
        return {
            is_success: false,
            status_code: 429,
            message: "Bạn đang gửi quá nhiều yêu cầu. Vui lòng quay lại sau ít phút.",
        };
    }
    if (err?.response?.data) {
        return err.response.data as IBaseRespone;
    }
    // Axios timeout: code === "ECONNABORTED" và message chứa "timeout".
    // Phân biệt với network fail thật để UX message rõ hơn.
    if (err?.code === "ECONNABORTED") {
        return { is_success: false, status_code: 504, message: "Yêu cầu xử lý quá lâu. Vui lòng thử lại." };
    }
    return { is_success: false, status_code: 500, message: err?.message || "Lỗi kết nối máy chủ." };
};

export const apiClient = {
    get: async <T = any>(path: string, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        const blocked = periodGuard<T>(path);
        if (blocked) return blocked;
        try {
            const res = await axios.get(`${appConst.baseApiURL}/${path}`, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
    post: async <T = any>(path: string, data?: any, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        const blocked = periodGuard<T>(path);
        if (blocked) return blocked;
        try {
            const res = await axios.post(`${appConst.baseApiURL}/${path}`, data || {}, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
    put: async <T = any>(path: string, data?: any, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        const blocked = periodGuard<T>(path);
        if (blocked) return blocked;
        try {
            const res = await axios.put(`${appConst.baseApiURL}/${path}`, data || {}, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
    delete: async <T = any>(path: string, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        const blocked = periodGuard<T>(path);
        if (blocked) return blocked;
        try {
            const res = await axios.delete(`${appConst.baseApiURL}/${path}`, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
};
